import {useEffect, useState} from 'react';
import {useDriveCall} from '../drive/queries';
import {getFileMediaBlob, getRevisionMediaBlob} from '../drive/driveApi';
import {getCachedImage, loadImage, peekCachedImage, prefetchImage} from './blobCache';

interface UseImageBlobResult {
    url: string | undefined;
    isLoading: boolean;
    error: Error | null;
    blob: Blob | undefined;
}

// The cache key of a stored version of a file; Drive ids never contain '@'.
function revisionKey(fileId: string, revisionId: string): string {
    return `${fileId}@${revisionId}`;
}

function fetchByKey(token: string, key: string): Promise<Blob> {
    const [fileId, revisionId] = key.split('@');
    return revisionId ? getRevisionMediaBlob(token, fileId, revisionId) : getFileMediaBlob(token, fileId);
}

/** Loads the full-res image cached under `key` (a file id, or a file version's `revisionKey`). */
function useCachedImageBlob(key: string | undefined): UseImageBlobResult {
    const callDrive = useDriveCall();
    // What this hook loaded and what failed, each kept with its key so another image or error is
    // never returned while the next one loads.
    const [loaded, setLoaded] = useState<{ key: string; url: string; blob: Blob }>();
    const [failure, setFailure] = useState<{ key: string; error: Error } | null>(null);

    useEffect(() => {
        if (!key || getCachedImage(key)) return;

        let cancelled = false;
        setFailure(null);
        loadImage(key, () => callDrive((token) => fetchByKey(token, key)))
            .then((entry) => {
                if (!cancelled) setLoaded({key, url: entry.objectUrl, blob: entry.blob});
            })
            .catch((err) => {
                if (cancelled) return;
                setFailure({key, error: err instanceof Error ? err : new Error('A kép betöltése sikertelen.')});
            });
        return () => {
            cancelled = true;
        };
    }, [key, callDrive]);

    // A cached image (prefetched, or loaded earlier) is there from the first render for its key.
    const cached = key ? peekCachedImage(key) : undefined;
    const own = loaded && loaded.key === key ? loaded : undefined;
    const url = cached?.objectUrl ?? own?.url;
    const blob = cached?.blob ?? own?.blob;
    const error = !url && failure && failure.key === key ? failure.error : null;
    return {url, blob, error, isLoading: !!key && !url && !error};
}

/** Loads the full-res image for `fileId` (from cache if possible) and warms the cache for its neighbors. */
export function useImageBlob(fileId: string | undefined, prevId?: string, nextId?: string): UseImageBlobResult {
    const callDrive = useDriveCall();
    const result = useCachedImageBlob(fileId);

    useEffect(() => {
        if (prevId) prefetchImage(prevId, () => callDrive((token) => getFileMediaBlob(token, prevId)));
        if (nextId) prefetchImage(nextId, () => callDrive((token) => getFileMediaBlob(token, nextId)));
    }, [prevId, nextId, callDrive]);

    return result;
}

/** Loads a stored version of a file in full resolution (cached like the originals). */
export function useRevisionImageBlob(fileId: string | undefined, revisionId: string | undefined): UseImageBlobResult {
    return useCachedImageBlob(fileId && revisionId ? revisionKey(fileId, revisionId) : undefined);
}
